import { z } from "zod";
import { ASSIGNABLE_ROLES, PERMISSION_KEYS } from "./permissions.js";

const optionalString = z.string().optional().transform((value) => value?.trim() || undefined);
const optionalEmail = z.string().optional().transform((value) => value?.trim() || undefined).pipe(z.string().email("validation_email").optional());
const optionalInt = z.number().int().positive().optional();
const coercedBoolean = z.preprocess((value) => {
  if (value === "true" || value === "1" || value === 1) return true;
  if (value === "false" || value === "0" || value === 0) return false;
  return value;
}, z.boolean());
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, "validation_date");
const optionalDate = z.string().optional().transform((value) => value || undefined).pipe(isoDate.optional());

export const customQuestionSchema = z.object({
  id: z.string().trim().min(1).max(100),
  title: z.string().trim().min(1).max(500),
  type: z.enum(["text", "textarea", "select", "boolean"]),
  options: z.array(z.string().trim().min(1).max(300)).max(100).optional(),
  required: z.boolean().optional(),
  disqualifyValue: z.string().max(300).optional(),
}).superRefine((value, context) => {
  if (value.type === "select" && !value.options?.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "api_question_options_required" });
  if (value.options && new Set(value.options).size !== value.options.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "api_duplicate_question_options" });
});

export type CustomQuestion = z.infer<typeof customQuestionSchema>;

export const publicFormDefaults = {
  askPhone: true,
  askLocation: true,
  askCdl: true,
  askExperience: true,
  askOtrExperience: false,
  askEndorsements: false,
  askSafetyHistory: true,
  askDocuments: true,
  customQuestions: [] as CustomQuestion[],
};

export const publicFormSchema = z.object({
  askPhone: coercedBoolean.default(true),
  askLocation: coercedBoolean.default(true),
  askCdl: coercedBoolean.default(true),
  askExperience: coercedBoolean.default(true),
  askOtrExperience: coercedBoolean.default(false),
  askEndorsements: coercedBoolean.default(false),
  askSafetyHistory: coercedBoolean.default(true),
  askDocuments: coercedBoolean.default(true),
  customQuestions: z.array(customQuestionSchema).default([]),
}).superRefine((value, context) => {
  const ids = value.customQuestions.map((question) => question.id);
  if (new Set(ids).size !== ids.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ["customQuestions"], message: "api_duplicate_question_ids" });
});

export const loginSchema = z.object({
  email: z.string().trim().min(1, "validation_email_required"),
  password: z.string().min(1, "validation_password_required"),
});

export const candidateSchema = z.object({
  firstName: z.string().trim().min(1, "validation_first_name_required").max(100),
  lastName: z.string().trim().min(1, "validation_last_name_required").max(100),
  phone: optionalString,
  email: optionalEmail,
  city: optionalString,
  state: optionalString,
  source: optionalString,
  preferredContact: optionalString,
  currentVacancyId: optionalInt,
  recruiterId: optionalInt,
  stageId: optionalInt,
  cdlClass: optionalString,
  cdlNumber: optionalString,
  cdlState: optionalString,
  endorsements: optionalString,
  cdlExpiryDate: optionalDate,
  medicalCardNumber: optionalString,
  medicalCardExpiry: optionalDate,
  totalExperienceMonths: z.coerce.number().int().min(0).default(0),
  otrExperienceMonths: z.coerce.number().int().min(0).default(0),
  desiredSalary: optionalString,
  preferredRoute: optionalString,
  desiredHomeTime: optionalString,
  willingToRelocate: coercedBoolean.default(false),
  availableDate: optionalDate,
  candidateComment: optionalString,
  internalNotes: optionalString,
  accidents: optionalString,
  violations: optionalString,
  qualificationAnswers: z.record(z.unknown()).optional(),
});

export const vacancySchema = z.object({
  title: z.string().trim().min(2, "validation_title_required").max(200),
  status: z.enum(["Draft", "Active", "Paused", "Closed", "Archived"]).default("Draft"),
  driversRequired: z.coerce.number().int().min(1).max(10000).default(1),
  terminalId: optionalInt,
  recruiterId: optionalInt,
  openDate: optionalDate,
  targetCloseDate: optionalDate,
  budgetCents: z.coerce.number().int().min(0).max(1_000_000_000).default(0),
  description: optionalString,
  jobTypes: z.array(z.string()).default([]),
  geography: z.record(z.unknown()).default({}),
  pay: z.record(z.unknown()).default({}),
  schedule: z.record(z.unknown()).default({}),
  requirements: z.record(z.unknown()).default({}),
  benefits: z.record(z.unknown()).default({}),
  formConfig: publicFormSchema.default(publicFormDefaults),
});

/** The single hiring profile: what applicants see and where the link points. */
export const hiringProfileSchema = z.object({
  title: z.string().trim().min(2, "validation_title_required").max(200),
  description: optionalString,
  isOpen: coercedBoolean.default(true),
  driversRequired: z.coerce.number().int().min(1).max(10000).default(1),
  recruiterId: optionalInt,
  pay: z.record(z.unknown()).default({}),
  schedule: z.record(z.unknown()).default({}),
  formConfig: publicFormSchema.default(publicFormDefaults),
});

/** Who we accept. Org-level, so there is exactly one answer per company. */
export const hiringCriteriaSchema = z.object({
  cdl_required: coercedBoolean.default(true),
  cdl_class: z.string().trim().max(4).optional(),
  minimum_experience_months: z.coerce.number().int().min(0).max(600).default(12),
  max_accidents: z.coerce.number().int().min(0).max(20).nullable().default(1),
  max_violations: z.coerce.number().int().min(0).max(20).nullable().default(2),
  medical_card_required: coercedBoolean.default(true),
  required_endorsements: z.array(z.string()).default([]),
  allowed_states: z.array(z.string()).default([]),
});

const settingsList = z.array(z.string().trim().min(1).max(180)).max(200);

export const organizationSettingsPatchSchema = z.object({
  qualification_rules: hiringCriteriaSchema.partial().optional(),
  risk_rules: z.object({
    expired_cdl: z.coerce.number().finite().min(0).max(1000),
    missing_medical_card: z.coerce.number().finite().min(0).max(1000),
    serious_violation: z.coerce.number().finite().min(0).max(1000),
    accident: z.coerce.number().finite().min(0).max(1000),
    employment_gap: z.coerce.number().finite().min(0).max(1000),
    insufficient_experience: z.coerce.number().finite().min(0).max(1000),
    incomplete_documents: z.coerce.number().finite().min(0).max(1000),
  }).partial().optional(),
  required_documents: settingsList.optional(),
  candidate_sources: settingsList.optional(),
  document_types: settingsList.optional(),
  task_categories: settingsList.optional(),
}).strict();

const stageFinalType = z.enum(["hired", "rejected", "withdrawn", "archived"]);
const stageColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const stageCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  color: stageColor.default("#64748b"),
  isFinal: z.boolean().default(false),
  finalType: stageFinalType.nullable().optional(),
}).superRefine((value, context) => {
  if (value.isFinal && !value.finalType) context.addIssue({ code: z.ZodIssueCode.custom, path: ["finalType"], message: "api_final_stage_type_required" });
  if (!value.isFinal && value.finalType) context.addIssue({ code: z.ZodIssueCode.custom, path: ["finalType"], message: "api_non_final_stage_type" });
});

export const stageUpdateSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  color: stageColor.optional(),
  isFinal: z.boolean().optional(),
  finalType: stageFinalType.nullable().optional(),
}).strict();

export const stageReorderSchema = z.object({
  ids: z.array(z.coerce.number().int().positive()).min(1).max(200),
}).refine((value) => new Set(value.ids).size === value.ids.length, { path: ["ids"], message: "api_duplicate_stage_ids" });

export const vacancySourceSchema = z.object({
  name: z.string().trim().min(2, "validation_source_name_required").max(160),
  spendCents: z.coerce.number().int().min(0).max(1_000_000_000).default(0),
});

export const vacancySourceUpdateSchema = z.object({
  isActive: z.boolean().optional(),
  spendCents: z.coerce.number().int().min(0).max(1_000_000_000).optional(),
}).strict().refine((value) => Object.keys(value).length > 0, "api_empty_update");

export const documentMetadataSchema = z.object({
  type: z.string().trim().min(1).max(80).default("Other"),
  name: z.string().trim().min(1).max(255),
  number: optionalString.pipe(z.string().max(100).optional()),
  issueDate: optionalDate,
  expiryDate: optionalDate,
  comment: optionalString.pipe(z.string().max(5000).optional()),
});

export const vacancyBulkActionSchema = z.object({
  candidateIds: z.array(z.coerce.number().int().positive()).min(1).max(500),
  action: z.enum(["reject", "reserve"]),
});

export const stageMoveSchema = z.object({
  stageId: z.coerce.number().int().positive(),
  comment: optionalString,
});

export const taskSchema = z.object({
  title: z.string().trim().min(2).max(180),
  description: optionalString,
  candidateId: optionalInt,
  vacancyId: optionalInt,
  assigneeId: z.coerce.number().int().positive(),
  dueAt: z.coerce.date(),
  priority: z.enum(["Low", "Medium", "High", "Urgent"]).default("Medium"),
  status: z.enum(["New", "In Progress", "Completed", "Cancelled", "Overdue"]).default("New"),
  category: z.string().trim().default("Other"),
}).superRefine((task, context) => {
  if (task.category !== "Other" && !task.candidateId) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["candidateId"], message: "Candidate is required for a candidate task" });
  }
});

export const interviewSchema = z.object({
  candidateId: z.coerce.number().int().positive(),
  vacancyId: optionalInt,
  interviewerId: z.coerce.number().int().positive(),
  startsAt: z.coerce.date(),
  durationMinutes: z.coerce.number().int().min(10).max(480).default(30),
  format: z.enum(["Phone", "Video", "Office", "Other"]).default("Phone"),
  locationOrLink: optionalString,
  status: z.enum(["Scheduled", "Completed", "Cancelled", "Rescheduled", "No Show"]).default("Scheduled"),
  result: optionalString,
  notes: optionalString,
  nextStep: optionalString,
});

export const employeeSchema = z.object({
  // Messages are translation keys: zod's English defaults were rendered raw, so
  // a Russian workspace answered an empty surname with "String must contain at
  // least 1 character(s)".
  firstName: z.string().trim().min(1, "validation_first_name_required").max(100),
  lastName: z.string().trim().min(1, "validation_last_name_required").max(100),
  email: z.string().trim().min(1, "validation_email_required").email("validation_email").max(180),
  phone: optionalString,
  position: optionalString,
  role: z.enum(ASSIGNABLE_ROLES),
  permissions: z.array(z.enum(PERMISSION_KEYS)).optional(),
  terminalId: optionalInt,
  status: z.enum(["active", "blocked", "archived"]).default("active"),
  // Accounts can be created/updated with an explicit password.
  password: z.string().optional().transform((value) => value?.trim() || undefined).pipe(z.string().min(8, "spa_at_least_8_characters").optional()),
  resetPassword: z.boolean().optional(),
});

/**
 * Updates keep `role` optional. An account whose role sits outside
 * ASSIGNABLE_ROLES — `company_admin`, which is what gates the audit log — could
 * not be saved at all: the enum rejected the value the screen had just shown.
 * Omitting the field now means "leave the stored role alone".
 */
export const employeeUpdateSchema = employeeSchema.extend({ role: z.enum(ASSIGNABLE_ROLES).optional() });

export const profileSchema = z.object({
  firstName: z.string().trim().min(1, "validation_first_name_required").max(100),
  lastName: z.string().trim().min(1, "validation_last_name_required").max(100),
  email: z.string().trim().email("validation_email").max(180),
  phone: optionalString,
});

export type CandidateInput = z.infer<typeof candidateSchema>;
export type VacancyInput = z.infer<typeof vacancySchema>;
export type HiringProfileInput = z.infer<typeof hiringProfileSchema>;
export type HiringCriteriaInput = z.infer<typeof hiringCriteriaSchema>;
export type PublicFormConfig = z.infer<typeof publicFormSchema>;
export type TaskInput = z.infer<typeof taskSchema>;
export type InterviewInput = z.infer<typeof interviewSchema>;
